import { IcShareHoloview } from '@assets/icons/32x32';
import { VStack } from '@chakra-ui/react';
import DataSheetCardLink from '@components/common/layout/DataSheetCardLink';
import { useMapContext } from '@components/map/MapContext';
import PopupContainer from '@components/map/popup/common/PopupContainer';
import TacticalClearanceSelect from '@components/map/popup/infos/targets/drone/TacticalClearanceSelect';
import { useAppSelector } from '@hooks/redux.hooks';
import { useWithDispatch } from '@hooks/useWithDispatch';
import { hasAnySensorRoleFromActiveProfile, hasRole, selectActiveOrganization } from '@redux/authent/authent.selectors';
import { updatePopupByMapId } from '@redux/maps/maps.reducer';
import { getUniqueSensorFamilies, isDrone, isRemoteControl } from '@utils/target.utils';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { RoleEnum } from '@/types/authent/roles.types';
import { Target } from '@/types/c2/c2.types';
import { ActionEnum, MarkEnum } from '@/types/commons/commons.types';
import { PopupDataType, PopupTypeEnum, ShareHoloviewPopupData } from '@/types/popup.types';
import { AbstractLocation, SensorAction } from '@/types/sensor/sensor.types';

import InfoValue from '../../InfoValue';
import ClassificationSelect from './ClassificationSelect';
import IdentificationSelect from './IdentificationSelect';

type Props = {
  target: Target<AbstractLocation>;
  position: { x: number; y: number };
};

export default function TargetActions({ target, position }: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const { mapId } = useMapContext();
  const updatePopup = useWithDispatch(updatePopupByMapId);
  const canManageTacticalClearance = useAppSelector((state) => hasRole(state, RoleEnum.MANAGE_TACTICAL_CLEARANCE));
  const sensorFamilies = getUniqueSensorFamilies(target.trackIds).map(
    (family) => ({ action: ActionEnum.MANAGE_TARGET, sensorFamily: family }) as SensorAction,
  );
  const canManageTarget = useAppSelector((state) => hasAnySensorRoleFromActiveProfile(state, sensorFamilies));
  const canManageClassification = useAppSelector((state) => hasRole(state, RoleEnum.MANAGE_CLASSIFICATION));
  const canMarkTracks = useAppSelector((state) => hasRole(state, RoleEnum.MARK_TRACKS));
  const organization = useAppSelector(selectActiveOrganization);

  const openPopup = useCallback(
    (popupDataType: PopupDataType) => {
      updatePopup({
        mapId,
        popup: {
          ...popupDataType,
          position,
        },
      });
    },
    [position, mapId, updatePopup],
  );

  function handleShareTargetHoloview() {
    openPopup({
      type: PopupTypeEnum.SHARE_HOLOVIEW,
      data: {
        site: target.site,
        trackIds: target.trackIds,
      } as ShareHoloviewPopupData,
    });
  }

  return (
    <VStack gap={0.25} width="100%" height="100%">
      <PopupContainer>
        <VStack width="100%" gap="standardS">
          <InfoValue
            label={formatMessage({ id: 'targetInfos.tab.actions.identification' })}
            value={
              <IdentificationSelect
                identification={target.identification}
                site={target.site}
                trackIds={target.trackIds}
                status={target.status}
                isDisabled={!canManageTarget}
              />
            }
          />
          <InfoValue
            label={formatMessage({ id: 'targetInfos.tab.actions.classification' })}
            value={
              <ClassificationSelect
                classification={target.classification}
                site={target.site}
                trackIds={target.trackIds}
                status={target.status}
                isDisabled={
                  !canManageTarget ||
                  !canManageClassification ||
                  (!isDrone(target, organization) && !isRemoteControl(target, organization))
                }
              />
            }
          />
          <InfoValue
            label={formatMessage({ id: 'targetInfos.tab.actions.tacticalClearance' })}
            value={
              <TacticalClearanceSelect
                tacticalClearance={target.tacticalClearance}
                site={target.site}
                trackIds={target.trackIds}
                status={target.status}
                isDisabled={!canManageTacticalClearance || !isDrone(target, organization)}
              />
            }
          />
        </VStack>
      </PopupContainer>
      {canMarkTracks && (
        <DataSheetCardLink
          label={formatMessage({ id: 'contextmenu.actions.target.broadcastTarget.shareHoloview' })}
          icon={IcShareHoloview}
          isDisabled={target.marks.some((mark) => mark.type === MarkEnum.CRISIS)}
          onClick={handleShareTargetHoloview}
        />
      )}
    </VStack>
  );
}
